/**
 * Students Service - API client for student records, CSV import,
 * parent linking, and portal credential endpoints
 */
import api from '../utils/api';

const studentsService = {
  // ---- Students ----

  listStudents: async (params = {}) => {
    try {
      return await api.get('/api/students', { params });
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  },

  getStudent: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}`);
    } catch (error) {
      console.error('Error fetching student:', error);
      throw error;
    }
  },

  createStudent: async (data) => {
    try {
      return await api.post('/api/students', data);
    } catch (error) {
      console.error('Error creating student:', error);
      throw error;
    }
  },

  updateStudent: async (studentId, data) => {
    try {
      return await api.put(`/api/students/${studentId}`, data);
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  },

  importStudents: async (formData) => {
    try {
      return await api.post('/api/students/import', formData);
    } catch (error) {
      console.error('Error importing students:', error);
      throw error;
    }
  },

  promoteStudents: async (actions) => {
    try {
      return await api.post('/api/students/promote', { actions });
    } catch (error) {
      console.error('Error promoting students:', error);
      throw error;
    }
  },

  getStudentEnrollments: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/enrollments`);
    } catch (error) {
      console.error('Error fetching student enrollment history:', error);
      throw error;
    }
  },

  getExitClearance: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/exit-clearance`);
    } catch (error) {
      console.error('Error fetching exit clearance:', error);
      throw error;
    }
  },

  exitStudent: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/exit`, data);
    } catch (error) {
      console.error('Error recording student exit:', error);
      throw error;
    }
  },

  deactivateStudent: async (studentId, reason) => {
    try {
      return await api.post(`/api/students/${studentId}/deactivate`, { reason });
    } catch (error) {
      console.error('Error deactivating student:', error);
      throw error;
    }
  },

  reactivateStudent: async (studentId) => {
    try {
      return await api.post(`/api/students/${studentId}/reactivate`, {});
    } catch (error) {
      console.error('Error reactivating student:', error);
      throw error;
    }
  },

  reEnrollStudent: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/re-enroll`, data);
    } catch (error) {
      console.error('Error re-enrolling student:', error);
      throw error;
    }
  },

  uploadStudentPhoto: async (studentId, formData) => {
    try {
      return await api.post(`/api/students/${studentId}/photo`, formData);
    } catch (error) {
      console.error('Error uploading student photo:', error);
      throw error;
    }
  },

  // ---- Siblings ----

  getSiblings: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/siblings`);
    } catch (error) {
      console.error('Error fetching student siblings:', error);
      throw error;
    }
  },

  addSibling: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/siblings`, data);
    } catch (error) {
      console.error('Error adding sibling:', error);
      throw error;
    }
  },

  removeSibling: async (studentId, siblingId) => {
    try {
      return await api.delete(`/api/students/${studentId}/siblings/${siblingId}`);
    } catch (error) {
      console.error('Error removing sibling:', error);
      throw error;
    }
  },

  // ---- Emergency Contacts ----

  getEmergencyContacts: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/emergency-contacts`);
    } catch (error) {
      console.error('Error fetching emergency contacts:', error);
      throw error;
    }
  },

  addEmergencyContact: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/emergency-contacts`, data);
    } catch (error) {
      console.error('Error adding emergency contact:', error);
      throw error;
    }
  },

  updateEmergencyContact: async (studentId, contactId, data) => {
    try {
      return await api.put(`/api/students/${studentId}/emergency-contacts/${contactId}`, data);
    } catch (error) {
      console.error('Error updating emergency contact:', error);
      throw error;
    }
  },

  removeEmergencyContact: async (studentId, contactId) => {
    try {
      return await api.delete(`/api/students/${studentId}/emergency-contacts/${contactId}`);
    } catch (error) {
      console.error('Error removing emergency contact:', error);
      throw error;
    }
  },

  // ---- Transfer Requests ----

  getTransferRequests: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/transfer-requests`);
    } catch (error) {
      console.error('Error fetching transfer requests:', error);
      throw error;
    }
  },

  createTransferRequest: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/transfer-requests`, data);
    } catch (error) {
      console.error('Error creating transfer request:', error);
      throw error;
    }
  },

  updateTransferRequest: async (studentId, transferId, data) => {
    try {
      return await api.put(`/api/students/${studentId}/transfer-requests/${transferId}`, data);
    } catch (error) {
      console.error('Error updating transfer request:', error);
      throw error;
    }
  },

  // ---- Parents ----

  getStudentParents: async (studentId) => {
    try {
      return await api.get(`/api/students/${studentId}/parents`);
    } catch (error) {
      console.error('Error fetching student parents:', error);
      throw error;
    }
  },

  addParentToStudent: async (studentId, data) => {
    try {
      return await api.post(`/api/students/${studentId}/parents`, data);
    } catch (error) {
      console.error('Error adding parent to student:', error);
      throw error;
    }
  },

  removeParentFromStudent: async (studentId, parentId) => {
    try {
      return await api.delete(`/api/students/${studentId}/parents/${parentId}`);
    } catch (error) {
      console.error('Error removing parent from student:', error);
      throw error;
    }
  },

  linkParentToStudent: async (studentId, parentId, custodyType) => {
    try {
      return await api.post(`/api/students/${studentId}/parents/${parentId}/link`, custodyType ? { custody_type: custodyType } : {});
    } catch (error) {
      console.error('Error linking parent to student:', error);
      throw error;
    }
  },

  updateCustodyType: async (studentId, parentId, custodyType) => {
    try {
      return await api.put(`/api/students/${studentId}/parents/${parentId}/custody`, { custody_type: custodyType });
    } catch (error) {
      console.error('Error updating custody type:', error);
      throw error;
    }
  },

  // ---- Portal credentials ----

  regenerateStudentCredentials: async (studentId) => {
    try {
      return await api.post(`/api/students/${studentId}/portal-credentials`);
    } catch (error) {
      console.error('Error regenerating student credentials:', error);
      throw error;
    }
  },

  regenerateParentCredentials: async (studentId, parentId) => {
    try {
      return await api.post(`/api/students/${studentId}/parents/${parentId}/portal-credentials`);
    } catch (error) {
      console.error('Error regenerating parent credentials:', error);
      throw error;
    }
  }
};

export default studentsService;
